import { useEffect, useRef, useMemo, useState } from 'react';
import { createChart, ColorType, CandlestickSeries, HistogramSeries, LineSeries } from 'lightweight-charts';

const CandlestickChart = ({ 
  data = [], 
  avgBuyPrice = 0, 
  height = 400,
  selectedPair = 'BTC-USD',
  duration = 60
}) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candlestickSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const maSeriesRef = useRef(null);
  const avgPriceLineRef = useRef(null);
  
  // State for OHLC display
  const [ohlcInfo, setOhlcInfo] = useState({
    open: 0,
    high: 0,
    low: 0,
    close: 0,
    change: 0,
    changePercent: 0,
    volume: 0,
    time: ''
  });

  // Convert price history data to OHLCV candlestick format
  // Uses real OHLC data from Coinbase API when available
  const candleData = useMemo(() => {
    if (!data || data.length < 2) return { candles: [], volumes: [], ma: [] };

    const candles = [];
    const volumes = [];
    const maValues = [];
    
    // Get base timestamp
    const baseTime = Math.floor(Date.now() / 1000) - (data.length * 60);
    
    // Process each data point
    data.forEach((point, idx) => {
      const price = point.price || point.close;
      if (!price || price === 0) return;
      
      // Check if we have real OHLC data from API
      const hasRealOHLC = point.open !== undefined && point.high !== undefined && 
                          point.low !== undefined && point.close !== undefined;
      
      let open, high, low, close;
      
      if (hasRealOHLC) {
        // Use real OHLC data from API
        open = point.open;
        high = point.high;
        low = point.low;
        close = point.close;
        
        // If OHLC are all identical (flat candle), add minimal variation for visibility
        if (open === high && high === low && low === close) {
          const variation = price * 0.001; // 0.1% variation for flat candles
          const prevPrice = idx > 0 ? (data[idx - 1]?.close || price) : price;
          
          if (price >= prevPrice) {
            // Bullish flat candle
            open = price - variation * 0.3;
            high = price + variation * 0.2;
            low = price - variation * 0.4;
            close = price;
          } else {
            // Bearish flat candle
            open = price + variation * 0.3;
            high = price + variation * 0.4;
            low = price - variation * 0.2;
            close = price;
          }
        }
      } else {
        // Fallback: Generate candle from price data (for WebSocket updates)
        const prevPrice = idx > 0 ? (data[idx - 1]?.price || price) : price;
        open = idx === 0 ? price : prevPrice;
        close = price;
        
        // Body bounds
        const bodyHigh = Math.max(open, close);
        const bodyLow = Math.min(open, close);
        
        // Small wicks for generated candles
        const tinyWick = price * 0.0001;
        high = bodyHigh + tinyWick;
        low = bodyLow - tinyWick;
      }
      
      const isUp = close >= open;
      
      // Use timestamp from data if available, otherwise calculate
      let timestamp;
      if (point.timestamp) {
        timestamp = Math.floor(new Date(point.timestamp).getTime() / 1000);
      } else {
        timestamp = baseTime + (idx * 60);
      }
      
      candles.push({
        time: timestamp,
        open: open,
        high: high,
        low: low,
        close: close,
        displayTime: point.time || '',
      });
      
      // Volume - use real volume if available, otherwise estimate
      const volumeValue = point.volume || (100 + Math.abs(close - open) * 1000);
      volumes.push({
        time: timestamp,
        value: volumeValue,
        color: isUp ? 'rgba(38, 166, 154, 0.6)' : 'rgba(239, 83, 80, 0.6)',
      });
      
      // Calculate Moving Average (9 period)
      if (idx >= 8) {
        const maWindow = candles.slice(idx - 8, idx + 1);
        const ma = maWindow.reduce((sum, c) => sum + c.close, 0) / maWindow.length;
        maValues.push({ time: timestamp, value: ma });
      }
    });

    // Sort all data by time in ascending order (required by lightweight-charts)
    candles.sort((a, b) => a.time - b.time);
    volumes.sort((a, b) => a.time - b.time);
    maValues.sort((a, b) => a.time - b.time);
    
    // Remove duplicates (same timestamp)
    const uniqueCandles = candles.filter((candle, idx, arr) => 
      idx === 0 || candle.time !== arr[idx - 1].time
    );
    const uniqueVolumes = volumes.filter((vol, idx, arr) => 
      idx === 0 || vol.time !== arr[idx - 1].time
    );
    const uniqueMa = maValues.filter((ma, idx, arr) => 
      idx === 0 || ma.time !== arr[idx - 1].time
    );

    return { candles: uniqueCandles, volumes: uniqueVolumes, ma: uniqueMa };
  }, [data]);

  // Update OHLC info when data changes
  useEffect(() => {
    if (candleData.candles.length > 0) {
      const lastCandle = candleData.candles[candleData.candles.length - 1];
      const firstCandle = candleData.candles[0];
      const change = lastCandle.close - firstCandle.open;
      const changePercent = firstCandle.open ? (change / firstCandle.open) * 100 : 0;
      const lastVolume = candleData.volumes[candleData.volumes.length - 1];
      
      setOhlcInfo({
        open: lastCandle.open,
        high: lastCandle.high,
        low: lastCandle.low,
        close: lastCandle.close,
        change: change,
        changePercent: changePercent,
        volume: lastVolume?.value || 0,
        time: lastCandle.displayTime || ''
      });
    }
  }, [candleData]);

  // Normalize locale
  const getValidLocale = () => {
    try {
      const browserLocale = navigator.language || 'en-US';
      const cleanLocale = browserLocale.replace(/@posix$/, '').replace(/_/g, '-');
      new Intl.Locale(cleanLocale);
      return cleanLocale;
    } catch (e) {
      return 'en-US';
    }
  };

  // Format price based on magnitude
  const formatPrice = (price) => {
    if (!price || price === 0) return '0.00';
    if (price < 0.0001) return price.toFixed(8);
    if (price < 0.01) return price.toFixed(6);
    if (price < 1) return price.toFixed(4);
    if (price < 100) return price.toFixed(2);
    return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Get price format settings
  const getPriceFormat = (price) => {
    if (!price || price === 0) return { precision: 2, minMove: 0.01 };
    if (price < 0.0001) return { precision: 8, minMove: 0.00000001 };
    if (price < 0.01) return { precision: 6, minMove: 0.000001 };
    if (price < 0.1) return { precision: 5, minMove: 0.00001 };
    if (price < 1) return { precision: 4, minMove: 0.0001 };
    return { precision: 2, minMove: 0.01 };
  };

  // Initialize chart
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: height,
      layout: {
        background: { type: ColorType.Solid, color: '#0d1117' },
        textColor: '#8b949e',
        fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
      },
      localization: {
        locale: getValidLocale(),
        timeFormatter: (timestamp) => {
          const date = new Date(timestamp * 1000);
          if (duration >= 10080) {
            return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
          }
          if (duration >= 1440) {
            return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
          }
          return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        },
      },
      grid: {
        vertLines: { color: '#21262d', style: 1 },
        horzLines: { color: '#21262d', style: 1 },
      },
      crosshair: {
        mode: 1,
        vertLine: {
          color: '#484f58',
          width: 1,
          style: 2,
          labelBackgroundColor: '#2d333b',
        },
        horzLine: {
          color: '#484f58',
          width: 1,
          style: 2,
          labelBackgroundColor: '#2d333b',
        },
      },
      rightPriceScale: {
        borderColor: '#21262d',
        scaleMargins: {
          top: 0.1,
          bottom: 0.2,
        },
        autoScale: true,
      },
      timeScale: {
        borderColor: '#21262d',
        timeVisible: true,
        secondsVisible: false,
        barSpacing: 14,
        minBarSpacing: 8,
        tickMarkFormatter: (timestamp) => {
          const date = new Date(timestamp * 1000);
          if (duration >= 10080) {
            return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
          }
          if (duration >= 1440) {
            return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
          }
          return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        },
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
      },
    });

    // Get price format based on data
    const samplePrice = data && data.length > 0 ? data[0]?.price : 100;
    const priceFormat = getPriceFormat(samplePrice);

    // Add candlestick series with Coinbase-like colors
    const candlestickSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderVisible: true,
      borderUpColor: '#26a69a',
      borderDownColor: '#ef5350',
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
      priceFormat: {
        type: 'price',
        precision: priceFormat.precision,
        minMove: priceFormat.minMove,
      },
    });

    // Add MA line series
    const maSeries = chart.addSeries(LineSeries, {
      color: '#f0b90b',
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });

    // Add volume series on separate scale
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: {
        type: 'volume',
      },
      priceScaleId: 'volume',
    });
    
    // Configure volume scale
    chart.priceScale('volume').applyOptions({
      scaleMargins: {
        top: 0.85,
        bottom: 0,
      },
    });

    // Subscribe to crosshair move for OHLC display
    chart.subscribeCrosshairMove((param) => {
      if (param.time && candlestickSeriesRef.current) {
        const candlePrice = param.seriesData.get(candlestickSeriesRef.current);
        const volumeData = param.seriesData.get(volumeSeriesRef.current);
        
        if (candlePrice) {
          const firstCandle = candleData.candles[0];
          const change = candlePrice.close - (firstCandle?.open || candlePrice.open);
          const changePercent = firstCandle?.open ? (change / firstCandle.open) * 100 : 0;
          
          // Format the time
          const date = new Date(param.time * 1000);
          const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          
          setOhlcInfo({
            open: candlePrice.open,
            high: candlePrice.high,
            low: candlePrice.low,
            close: candlePrice.close,
            change: change,
            changePercent: changePercent,
            volume: volumeData?.value || 0,
            time: timeStr
          });
        }
      }
    });

    chartRef.current = chart;
    candlestickSeriesRef.current = candlestickSeries;
    volumeSeriesRef.current = volumeSeries;
    maSeriesRef.current = maSeries;

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ 
          width: chartContainerRef.current.clientWidth 
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, [height, selectedPair, data?.length, duration]); // Rebuild chart when pair changes, data length changes, or duration changes

  // Update data
  useEffect(() => {
    if (!candlestickSeriesRef.current || !volumeSeriesRef.current) return;
    
    if (candleData.candles.length > 0) {
      // Update price format based on current data
      const samplePrice = candleData.candles[0]?.close || 100;
      const priceFormat = getPriceFormat(samplePrice);
      
      candlestickSeriesRef.current.applyOptions({
        priceFormat: {
          type: 'price',
          precision: priceFormat.precision,
          minMove: priceFormat.minMove,
        },
      });
      
      candlestickSeriesRef.current.setData(candleData.candles);
      volumeSeriesRef.current.setData(candleData.volumes);
      
      if (maSeriesRef.current && candleData.ma.length > 0) {
        maSeriesRef.current.setData(candleData.ma);
      }
      
      if (chartRef.current) {
        chartRef.current.timeScale().fitContent();
      }
    }
  }, [candleData]);

  // Update average buy price line
  useEffect(() => {
    if (!candlestickSeriesRef.current) return;

    if (avgPriceLineRef.current) {
      candlestickSeriesRef.current.removePriceLine(avgPriceLineRef.current);
      avgPriceLineRef.current = null;
    }

    if (avgBuyPrice > 0) {
      avgPriceLineRef.current = candlestickSeriesRef.current.createPriceLine({
        price: avgBuyPrice,
        color: '#00BFFF',
        lineWidth: 1,
        lineStyle: 2,
        axisLabelVisible: true,
        title: 'AVG',
      });
    }
  }, [avgBuyPrice]);

  const isPositive = ohlcInfo.change >= 0;

  return (
    <div className="relative bg-[#0d1117] rounded-lg overflow-hidden">
      {/* OHLC Info Bar - Coinbase style */}
      <div className="flex items-center gap-3 px-3 py-2 text-xs font-mono border-b border-[#21262d] flex-wrap">
        <span className="text-gray-500">{ohlcInfo.time}</span>
        <span className="text-gray-400">
          O<span className="text-white ml-1">{formatPrice(ohlcInfo.open)}</span>
        </span>
        <span className="text-gray-400">
          H<span className="text-[#26a69a] ml-1">{formatPrice(ohlcInfo.high)}</span>
        </span>
        <span className="text-gray-400">
          L<span className="text-[#ef5350] ml-1">{formatPrice(ohlcInfo.low)}</span>
        </span>
        <span className="text-gray-400">
          C<span className="text-white ml-1">{formatPrice(ohlcInfo.close)}</span>
        </span>
        <span className={`ml-auto ${isPositive ? 'text-[#26a69a]' : 'text-[#ef5350]'}`}>
          {isPositive ? '+' : ''}{formatPrice(ohlcInfo.change)} ({isPositive ? '+' : ''}{ohlcInfo.changePercent.toFixed(2)}%)
        </span>
      </div>
      
      {/* Chart Container */}
      <div 
        ref={chartContainerRef} 
        data-testid="candlestick-chart"
        className="w-full"
        style={{ minHeight: height }}
      />
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-4 py-2 text-xs text-gray-500 border-t border-[#21262d]">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-[#26a69a]"></div>
          <span>Bullish</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-[#ef5350]"></div>
          <span>Bearish</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-0.5 bg-[#f0b90b]"></div>
          <span>MA(9)</span>
        </div>
        {avgBuyPrice > 0 && (
          <div className="flex items-center gap-1">
            <div className="w-4 h-0.5 bg-[#00BFFF]" style={{ borderStyle: 'dashed' }}></div>
            <span>Avg Buy</span>
          </div>
        )}
      </div>
      
      {/* No data message */}
      {(!data || data.length < 2) && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#0d1117]/90">
          <div className="text-gray-500 text-sm font-mono">
            Waiting for price data...
          </div>
        </div>
      )}
    </div>
  );
};

export default CandlestickChart;
